<template>
    <div class="stats-chart">
        <ui-card class="chart-box" :title="title" :icon="icon" :height="height">
            <div class="chart-canvas" ref="chart"></div>
            <div class="chart-empty" v-if="Array.isArray(chartData) && !chartData.length">
                <i class="nucfont inuc-empty-file"></i>暂无数据
            </div>
        </ui-card>
    </div>
</template>

<script>
    /**
     *  具体使用方法见 `/doc/statsChart.md`
     *  一、chart组件
     *      参数：title、type
     *
     *      type
     *      pie： 普通饼图、内外环的二合一饼图
     *      bar： 柱状图、条形图
     *      line：折线图
     *      radar: 雷达图
     *
     *  二、使用方法：
     *     1、<stats-chart ref="myChart" type="pie" title="岗位资质与漏检物品种类"></stats-chart>
     *     2、this.refs.myChart.drawChart(options)  // options请根据图表类型看drawChart里的注释
     **/

    import uiCard from '@/components/Statistics/StatisticUICard.vue'
    import {Debounce} from '@/utils';

    const defaultOpts = {
        color: ['#59a4cb', '#f18d82', '#f2d989', '#37b7c5', '#998b7e', '#ab85e8', '#6a7882', '#c1837f', '#d2a470', '#43a7de', '#ac628d', '#a89a69', '#496c99', '#c478b7', '#7c6d77'],
        showLegend: false,
        isRing: false
    }

    import echarts from 'echarts'

    export default {
        props: {
            // 标题
            title: {
                type: String,
                required: true
            },
            // 图表类型 line/bar/pie/radar
            type: {
                type: String,
                required: true
            },
            // 表格高度
            height: {
                type: String,
                default: '320px'
            }
        },

        components: {
            uiCard
        },

        data() {
            return {
                chartData: null
            }
        },

        computed: {
            icon() {
                let result = {
                    'bar': 'inuc-bar-chart',
                    'pie': 'inuc-pie-chart',
                    'line': 'inuc-line-chart',
                    'radar': 'inuc-radar-chart',
                }[this.type]
                return result || 'inuc-list-chart'
            }
        },

        mounted() {
            this.initChart()

            // 使用nextTick保证下面代码晚于beforeDestroy执行
            this.$nextTick(() => {
                $(window).on('resize.chart', Debounce(() => {
                    this.chart.resize()
                }, 300))
            })

            this.$once('hook:beforeDestroy', () => {
                $(window).off('resize.chart')
            })
        },

        methods: {
            initChart() {
                this.chart = echarts.init(this.$refs.chart)

                this.chart.showLoading({
                    text: '',
                })
            },

            drawChart(options = {}) {
                this.chart.clear()

                let mixedOptions = Object.assign({}, defaultOpts, options)

                switch (this.type) {
                    case 'pie':
                        this.drawPieChart(mixedOptions)
                        break
                    case 'bar':
                        this.drawBarChart(mixedOptions)
                        break
                    case 'line':
                        this.drawLineChart(mixedOptions)
                        break
                    case 'radar':
                        this.drawRadarChart(mixedOptions)
                        break
                    default:
                        console.error('错误的type参数！')
                }
            },

            /**
             * 饼图
             * @param options
             *
             *        *必传项： data
             *         data 如果传数组 []，则为正常的饼图；如果传对象 {innerRing:[],outerRing:[]}，则为内环+外环的饼图
             *
             *        选传项：{name | tooltip | legend | showLegend | isRing | series }
             *
             */
            drawPieChart(options) {
                let data = this.chartData = options.data

                this.chart.hideLoading()

                if (!data) return

                let legendData = []
                let series = []

                if (data.innerRing && data.outerRing) {
                    if (!data.outerRing.length) {
                        this.chartData = []
                        return
                    }

                    legendData = [...data.innerRing.map(item => item.name), ...data.outerRing.map(item => item.name)]

                    // 解决id一样导致的图表残影问题
                    data.outerRing.forEach(item => delete item.id)

                    series = [
                        {
                            name: (options.name && options.name[0]) || this.title,
                            type: 'pie',
                            selectedMode: 'single',
                            radius: [0, '50%'],
                            label: {
                                normal: {
                                    position: 'inner'
                                },
                            },
                            labelLine: {
                                normal: {
                                    show: false
                                }
                            },
                            data: data.innerRing,
                            ...(options.series && options.series[0])
                        },
                        {
                            name: (options.name && options.name[1]) || this.title,
                            type: 'pie',
                            radius: ['60%', '80%'],
                            selectedMode: 'single',
                            label: {
                                normal: {
                                    position: 'outer',
                                }
                            },
                            data: data.outerRing,
                            ...(options.series && options.series[1])
                        }
                    ]

                } else {
                    legendData = data.map(item => item.name)

                    series = [{
                        name: options.name || this.title,
                        type: 'pie',
                        center: ['50%', '50%'],
                        data: data,
                        radius: options.isRing ?  ['55%', '75%']:  ['0', '75%'],
                        ...options.series
                    }]
                }

                let chartOption = {
                    color: options.color || defaultOpts.color || [],
                    tooltip: {
                        trigger: 'item',
                        formatter: "{a} <br/>{b}: {c} ({d}%)",
                        ...options.tooltip
                    },
                    legend: {
                        type: 'scroll',
                        icon: 'rect',
                        orient: 'vertical',
                        itemWidth: 25,
                        itemHeight: 4,
                        right: 0,
                        data: legendData,
                        pageButtonGap: 15,
                        pageButtonItemGap: 15,
                        pageIconSize: [10, 10],
                        show: options.showLegend,
                        ...options.legend
                    },
                    series
                }

                this.chart.setOption(chartOption)
            },

            /**
             * 柱状图/条形图
             * @param options = {}
             *
             *        *必传项：单列柱状图: data
             *                多列柱状图：series、category //类目轴 如：['周一', '周二', '周三', '周四']
             *
             *        选传项：{ legend | showLegend | xAxis | yAxis | dataZoom | reverse }
             *        reverse: 默认false，从左到右排列的柱状图；如果为true则为从上到下排列的条形图
             *
             */
            drawBarChart(options) {
                this.chart.hideLoading()

                let series = []
                let trigger = ''

                // 处理单列数据
                if (options.data) {
                    this.chartData = options.data
                    trigger = 'item'

                    if (!options.data.length) {
                        this.chartData = []
                        return
                    }

                    let data = options.data.map(item => item.value)

                    options.category = options.data.map(item => item.name)

                    series =  [{
                            name: options.name || this.title,
                            type: 'bar',
                            barMaxWidth: 40,
                            data,
                            ...options.series
                        }]

                // 多列数据
                } else if (options.series) {
                    this.chartData = series = options.series
                    trigger = 'axis'

                    // 每一项均为空
                    let isEmpty = this.chartData.every(item => !item.data.length)
                    if (isEmpty) {
                        this.chartData = []
                    }

                    /* 多列数据请直接传series 比如下面这个例子
                    let series = serverData.series.map(item => {
                        return {
                            name: item.name,
                            type: 'bar',
                            data: item.data
                        }
                    })
                     */
                }

                if (!this.chartData.length) return

                //坐标轴刻度标签的相关设置
                let axisLabel = {
                    interval: 0,
                        padding: [8, 0, 0, 0],
                    formatter: function (val) {
                        return val.length > 8 ? val.substr(0, 7) + "..." : val;
                    }
                }

                let chartOption = {
                    color: options.color || defaultOpts.color || [],
                    tooltip: {
                        trigger,
                        axisPointer: {            // 坐标轴指示器，坐标轴触发有效
                            type: 'shadow'        // 默认为直线，可选为：'line' | 'shadow'
                        },
                        ...options.tooltip
                    },
                    legend: {
                        show: options.showLegend,
                        icon: 'rect',
                        itemWidth: 25,
                        itemHeight: 5,
                        ...options.legend
                    },
                    grid: {
                        top: 20,
                        bottom: 20,
                        left: '5%',
                        right: '5%',
                        containLabel: true
                    },
                    xAxis: (() => {
                        if (options.reverse) {
                            return {
                                type: 'value',
                                ...options.xAxis,
                            }
                        } else {
                            return {
                                type: 'category',
                                data: options.category,
                                axisLabel,
                                ...options.xAxis,
                            }
                        }
                    })(),
                    yAxis: (() => {
                        if (options.reverse) {
                            return {
                                type: 'category',
                                data: options.category,
                                axisLabel,
                                ...options.yAxis
                            }
                        } else {
                            return {
                                type: 'value',
                                ...options.yAxis
                            }
                        }
                    })(),

                    dataZoom: options.dataZoom || [],

                    series
                }

                this.chart.setOption(chartOption)
            },

            /**
             * 折线图
             * @param options = {}
             *
             *        *必传项： data
             *
             *        选传项：{ name | tooltip | legend | showLegend | xAxis | yAxis | series }
             */
            drawLineChart(options) {
                this.chartData = options.data

                this.chart.hideLoading()

                if (!options.data.length) return

                let nameArr = options.data.map(item => item.name)
                let data = options.data.map(item => item.value)

                let chartOption = {
                    color: options.color || defaultOpts.color || [],
                    tooltip: {
                        trigger: 'axis',
                        ...options.tooltip
                    },
                    legend: {
                        show: options.showLegend,
                        ...options.legend
                    },
                    grid: {
                        top: 20,
                        bottom: 20,
                        left: '5%',
                        right: '5%',
                        containLabel: true
                    },
                    xAxis: {
                        type: 'category',
                        data: nameArr,
                        boundaryGap: false,
                        ...options.xAxis
                    },
                    yAxis: {
                        type: 'value',
                        ...options.yAxis
                    },
                    series: [{
                        name: options.name || this.title,
                        type: 'line',
                        smooth: true,
                        data,
                        ...options.series
                    }]
                }

                this.chart.setOption(chartOption)
            },

            /**
             * 雷达图
             * @param options = {}
             *
             *        *必传项： data
             *
             *        选传项：{ name | tooltip | legend | showLegend | series }
             */
            drawRadarChart(options) {
                this.chartData = options.data

                this.chart.hideLoading()

                if (!options.data.length) return

                let _data = [...options.data]
                let max = _data.sort((a, b) => b.value - a.value)[0]['value']

                let indicator = options.data.map(item => {
                    let name = item.name
                    if (name.length>8) {
                        name = name.substr(0, 7) + '...'
                    }
                    return {
                        name,
                        max
                    }
                })
                let data = options.data.map(item => item.value)

                let chartOption = {
                    color: options.color || defaultOpts.color || [],
                    tooltip: {
                        trigger: 'item',
                        ...options.tooltip
                    },
                    legend: {
                        show: options.showLegend,
                        ...options.legend
                    },
                    radar: {
                        indicator,
                        name: {
                            textStyle: {
                                color:'#444'
                            }
                        },
                        center: ['50%', '50%']
                    },
                    series: [{
                        type: 'radar',
                        areaStyle: {
                            normal: {
                                opacity: 0.8,
                                color: new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                                    {
                                        color: '#a4696a',
                                        offset: 0
                                    },
                                    {
                                        color: '#774080',
                                        offset: 0.4
                                    },
                                    {
                                        color: '#4a1795',
                                        offset: 1
                                    }
                                ])
                            }
                        },
                        data: [{
                            name: options.name || this.title,
                            value: data
                        }],
                        ...options.series
                    }]
                }

                this.chart.setOption(chartOption)
            },
        },
    }
</script>

<style lang="scss">
    .stats-chart {
        height: 100%;

        .chart-box {
            .card-body {
                position: relative;
                padding: 15px;
            }
            .chart-canvas {
                height: 100%;
            }
            .chart-empty {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #fff;
                color: #999;
                font-size: 15px;

                .nucfont {
                    margin-right: 4px;
                    vertical-align: -1px;
                }
            }
        }
    }
</style>
